import { create } from 'zustand';
import { MemberData, MemberFilterData, MemberSortData, OverallData } from '../../../shared/dto/EntityRepository';
import { ExposureLevel, MemberSortOrder, PerPage, SortDirection, SubscriptionStatus } from '../../../shared/enum/EnumRepository';


export type AdminMemberEmailTarget = Pick<MemberData, 'memberId' | 'username'>;

interface AdminMemberViewState {
  activeMember: MemberData | null;
  isEmailViewerOpen: boolean;
  selectedEmailMembers: AdminMemberEmailTarget[];
  setActiveMember: (member: MemberData | null) => void;
  openEmailViewer: (members: AdminMemberEmailTarget[]) => void;
  closeEmailViewer: () => void;
  setSelectedEmailMembers: (members: AdminMemberEmailTarget[]) => void;
  clearActiveMember: () => void;
}

export const useAdminMemberViewStore = create<AdminMemberViewState>((set) => ({
  activeMember: null,
  isEmailViewerOpen: false,
  selectedEmailMembers: [],
  setActiveMember: (activeMember) => set({ activeMember, isEmailViewerOpen: false }),
  openEmailViewer: (members) => set({ isEmailViewerOpen: true, selectedEmailMembers: members }),
  closeEmailViewer: () => set({ isEmailViewerOpen: false, selectedEmailMembers: [] }),
  setSelectedEmailMembers: (selectedEmailMembers) => set({ selectedEmailMembers }),
  clearActiveMember: () => set({ activeMember: null, isEmailViewerOpen: false, selectedEmailMembers: [] }),
}));


const initialMemberFilterData: MemberFilterData = {
  subscription: {
    [SubscriptionStatus.TRIALING]: true,
    [SubscriptionStatus.ACTIVE]: true,
    [SubscriptionStatus.CANCELED]: true,
  },
  isPublic: {
    [ExposureLevel.PUBLIC]: true,
    [ExposureLevel.SEARCHABLE]: true,
    [ExposureLevel.PRIVATE]: true,
  },
  lastLoginAt: { min: null, max: null },
};

interface AdminMemberListState {
  pageSize: PerPage;
  setPageSize: (pageSize: PerPage) => void;
  sortData: MemberSortData;
  setSortData: (sortData: MemberSortData) => void;
  filterData: MemberFilterData;
  setFilterData: (filterData: MemberFilterData) => void;
  memberList: MemberData[];
  setMemberList: (memberList: MemberData[]) => void;
}

export const useAdminMemberListStore = create<AdminMemberListState>((set) => ({
  pageSize: PerPage.PAGE_10,
  setPageSize: (pageSize) => set({ pageSize }),
  sortData: { sort: MemberSortOrder.USERNAME, direction: SortDirection.ASC },
  setSortData: (sortData) => set({ sortData }),
  filterData: initialMemberFilterData,
  setFilterData: (filterData) => set({ filterData }),
  memberList: [],
  setMemberList: (memberList) => set({ memberList }),
}));


interface AdminState {
  overall: OverallData;
  setOverall: (overall: OverallData) => void;
}

export const useAdminStore = create<AdminState>((set) => ({
  overall: {
    memberCount: 0,
    subscriptionCount: 0,
    storageOverall: {
      totalOriginBytes: 0,
      totalBytes: 0,
      totalFiles: 0,
    },
  },
  setOverall: (overall) => set({ overall }),
}));
